import { BellIcon, CheckIcon, InboxIcon, SettingsIcon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMe } from '@/features/auth/use-me';
import { useAutoMarkNotificationsForPath } from '@/features/notifications/use-auto-mark-notifications-for-path';
import { notificationTarget } from '@/features/notifications/notification-target';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/features/notifications/use-notifications';
import { isPortalAudience, isPortalPath } from '@/products/helpdesk/features/portal/portal-paths';
import { notificationBellLabel } from '@/lib/a11y';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { NOTIFICATIONS_PATH, PROFILE_PATH } from '@/platform/lib/paths';

export function NotificationBell() {
  const me = useMe();
  const location = useLocation();
  const list = useNotifications(true);
  useAutoMarkNotificationsForPath(true);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const navigate = useNavigate();
  const unread = list.data?.unreadCount ?? 0;
  const items = list.data?.items ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative"
          aria-label={notificationBellLabel(unread)}
        >
          <BellIcon aria-hidden />
          {unread > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] leading-none"
              aria-hidden
            >
              {unread > 9 ? '9+' : unread}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="p-0">Benachrichtigungen</DropdownMenuLabel>
          <Button
            variant="ghost"
            size="xs"
            disabled={unread === 0 || markAll.isPending}
            onClick={() => void markAll.mutateAsync()}
          >
            <CheckIcon />
            Alle gelesen
          </Button>
        </div>
        <DropdownMenuSeparator className="m-0" />
        <div className="max-h-80 overflow-y-auto">
          {list.isPending && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">Wird geladen …</p>
          )}
          {!list.isPending && items.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              Keine Benachrichtigungen
            </p>
          )}
          {items.map((item) => (
            <DropdownMenuItem
              key={item.id}
              className={cn('items-start gap-2 whitespace-normal', !item.readAt && 'bg-muted/60')}
              onSelect={() => {
                if (!item.readAt) {
                  void markRead.mutateAsync(item.id);
                }
                const target = notificationTarget(
                  item,
                  isPortalPath(location.pathname) || isPortalAudience(me.data) ? 'portal' : 'agent',
                );
                if (target) {
                  void navigate(target);
                }
              }}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{item.title}</span>
                <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                  {item.body}
                </span>
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  {formatDateTime(item.createdAt)}
                </span>
              </span>
            </DropdownMenuItem>
          ))}
        </div>
        <DropdownMenuSeparator className="m-0" />
        <DropdownMenuItem asChild>
          <Link to={NOTIFICATIONS_PATH} className="cursor-pointer">
            <InboxIcon />
            Alle anzeigen
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={PROFILE_PATH} className="cursor-pointer">
            <SettingsIcon />
            Benachrichtigungen einstellen
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
